// src/components/ui/Button/index.tsx
import clsx from "clsx";
import { useEffect, useRef } from "react";
import { DatePicker } from "../DatePicker";
import { Row } from "@/components";

type DatePickerRangeProps = {
  label?: string;
  startDateLabel?: string;
  endDateLabel?: string;
  startDate?: Date;
  endDate?: Date;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  onStartDateClick?: () => void;
  onEndDateClick?: () => void;
};

export const DatePickerRange = ({
  label,
  startDateLabel = "Start Date",
  endDateLabel = "End Date",
  startDate,
  endDate,
  className,
  disabled = false,
  required = false,
  error,
  onStartDateChange,
  onEndDateChange,
  onStartDateClick,
  onEndDateClick,
}: DatePickerRangeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // we need to generate a unique id for the container and manage errors
  const containerId = label
    ? `datepicker-range-${label.toLowerCase().replace(/\s+/g, "-")}`
    : "datepicker-range";

  useEffect(() => {
    if (error && containerRef.current) {
      containerRef.current.setAttribute("aria-invalid", "true");
      containerRef.current.setAttribute(
        "aria-describedby",
        `${containerId}-error`,
      );
    } else if (containerRef.current) {
      containerRef.current.setAttribute("aria-invalid", "false");
      containerRef.current.removeAttribute("aria-describedby");
    }
  }, [error, containerId]);

  const handleStartDateChange = (date: Date) => {
    // Ensure end date is not before start date
    if (endDate && date > endDate) {
      onStartDateChange(date);
      onEndDateChange(date);
    } else {
      onStartDateChange(date);
    }
  };

  const handleEndDateChange = (date: Date) => {
    // Ensure start date is not after end date
    if (startDate && date < startDate) {
      onStartDateChange(date);
      onEndDateChange(date);
    } else {
      onEndDateChange(date);
    }
  };

  return (
    <div
      ref={containerRef}
      className={clsx("base-col gap-x8", className)}
      role="group"
      aria-labelledby={label ? `${containerId}-label` : undefined}
      aria-describedby={error ? `${containerId}-error` : undefined}
    >
      {label && (
        <label id={`${containerId}-label`} className="text-sm text-dark">
          {label}
          {required && (
            <span className="text-error ml-1" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <Row gap={"x16"} role="group">
        <DatePicker
          label={startDateLabel}
          value={startDate}
          maxValue={endDate}
          disabled={disabled}
          required={required}
          onChange={handleStartDateChange}
          onClick={onStartDateClick}
          className="flex-1"
        />

        <DatePicker
          label={endDateLabel}
          value={endDate}
          minValue={startDate}
          disabled={disabled}
          required={required}
          onChange={handleEndDateChange}
          onClick={onEndDateClick}
          className="flex-1"
        />
      </Row>

      {error && (
        <div
          id={`${containerId}-error`}
          className="text-sm text-error"
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      )}
    </div>
  );
};
